import * as React from 'react';
import {Table} from 'antd';
import {ColumnProps} from 'antd/lib/table';

import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay
} from '../app/ApplicationContext';
import {ErrorAlert} from '../common/ErrorAlert';
import {ErrorResponse, IncomingInvoice, PageResponseEnvelope} from '../api/dtos/dtos';
import furoApi from '../api/FuroApi';
import locales from '../locales';
import * as tableUtils from '../utils/table-utils';
import {GridState, searchableSortableColumn, sortableColumn} from '../utils/table-utils';
import {alignRightNoWrap, groupNumber} from '../utils/number-utils';
import {formatDate} from '../utils/date-utils';

interface Props {
  onSelect: (invoice: IncomingInvoice) => void;
}

interface State extends GridState<IncomingInvoice> {
  error?: ErrorResponse;
}

class IncomingInvoiceSelector extends React.Component<Props, State> {

  public readonly loadingHandler?: LoadingOverlayHandler;

  public state: State = {
    ...tableUtils.initialGridState(10),
    sortColumnKey: 'name',
    sortOrder: 'ascend',
  };

  public componentDidMount() {
    this.reload();
  }

  public render() {
    const columns: Array<ColumnProps<IncomingInvoice>> = [
      searchableSortableColumn(locales.invoice_number, 'invoiceNumber', this.state, this.renderInvoiceNumberColumn),
      searchableSortableColumn(locales.appellation, 'name', this.state, i => i.name),
      searchableSortableColumn(locales.company_name, 'companyName', this.state, i => i.companyName),
      searchableSortableColumn(locales.bankAccount, 'bankAccountNumber', this.state, i => i.bankAccountNumber),
      sortableColumn(locales.issue_date, 'issueDate', this.state, i => formatDate(i.issueDate)),
      sortableColumn(locales.sum, 'sum', this.state, this.renderSumWithCurrency),
    ];

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <Table
          dataSource={this.state.data}
          columns={columns}
          rowKey={tableUtils.idRowKeyFn}
          bordered={true}
          size="small"
          pagination={tableUtils.defaultPaginationConfig(this.state)}
          onChange={tableUtils.handleOnGridChange(this.state, this.onGridChange)}
          scroll={{x: 1024}}
        />

        <ErrorAlert style={{marginTop: '16px'}} error={this.state.error}/>
      </>
    );
  }

  private renderInvoiceNumberColumn = (invoice: IncomingInvoice): React.ReactNode => {
    const onClick = () => this.props.onSelect(invoice);
    return <a onClick={onClick}>{invoice.invoiceNumber}</a>;
  };

  private renderSumWithCurrency = (invoice: IncomingInvoice): React.ReactNode => {
    const unit = invoice.currency === 'EUR' ? locales.currency_eur : locales.app_currency;
    return alignRightNoWrap(<>{groupNumber(invoice.sum)} {unit}</>);
  };

  private onGridChange = (newState: GridState<IncomingInvoice>) => {
    this.setState({...newState}, () => this.reload());
  };

  private reload = () => {
    const request = tableUtils.toGridRequest(this.state);
    const promise = furoApi.incomingInvoices.getAll(request)
      .then(this.onReloadSuccess)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onReloadSuccess = (response: PageResponseEnvelope<IncomingInvoice>) => {
    this.setState({
      ...tableUtils.toGridState(response),
      error: undefined,
    });
  };

  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  };
}

export default IncomingInvoiceSelector;




// WEBPACK FOOTER //
// ./src/inventory-stock-details/IncomingInvoiceSelector.tsx